// פאנלי הצד (10 §1, 11 §1). ai2 — לפי מה שהמשתמש בא בשבילו: ממתין לך (קונסנט/טיוטה/ריצה שנכשלה),
// מה בנינו בשיחה הזו, היצירות שלי, הקשר נתונים (בלי מונים שאינם ניתנים לפעולה). ai1 — לפי מה שמחכה לאדם:
// בקשות פרטי חיבור (רק כשיש צימוד שבינה-1 הנפיקה וממתין לסודות; POST /api/connector/pairings/id/:id/secrets —
// הסיסמה לא נשמרת בדפדפן), הצעות ממתינות (אשר/דחה → POST /api/tree/proposals/:id/decide בזהות האדם),
// עולמות (עם התראות קודם) עם שורת סנכרון אחת לעולם שנפתחת לריצות, ומשתמשים.
// conversation-admin-requests: "בקשות משתמשים" (ai1: טפל בזה / דחה עם סיבה → POST /api/admin-requests/:id/decide בזהות האדם;
// טפל בזה עובר לשיחת מנהל הבינה שנפתחה) ו"בקשות למנהל המערכת" (ai2: ממתין למנהל / המנהל מטפל / טופל / נדחה).
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { aiApi } from '../../lib/aiApi.ts';
import { useSession } from '../../lib/auth.ts';
import type { AdminRequestCard, MyAdminRequest, PanelAi1Full, PanelAi2Full, PendingPairing, PendingProposal, RecentProposal } from '../../lib/aiContract.ts';
import { ApiError } from '../../lib/api.ts';
import { T } from '../../lib/strings.ts';
import { fmtAgo, fmtClock, fmtWhen } from '../../lib/time.ts';
import { cn } from '../../lib/utils.ts';
import { StatusLabel } from '../ui/badge.tsx';
import { changesSentenceHe, evidenceSummaryHe, hasApiItems, humanizeEvidenceHe, proposalTitleHe, proposerHe, type ChangeItem } from '../../lib/proposals.ts';
import { consentHref } from '../ConsentDrawer.tsx';
import { Button } from '../ui/button.tsx';
import { Input, Label, Select } from '../ui/input.tsx';

function Section({ title, children, testId, count }: { title: string; children: ReactNode; testId?: string; count?: number }) {
  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <h2 className="m-0 flex items-center gap-2 text-base font-semibold">
        {title}
        {count !== undefined && <span className="rounded-sm bg-muted px-2 text-sm font-normal tabular-nums">{count}</span>}
      </h2>
      {children}
    </section>
  );
}

const when = (iso: string | null | undefined) => (iso ? <bdi dir="auto">{fmtWhen(iso)}</bdi> : '–');

/* ─── ai2 ─── */

type Ai2 = PanelAi2Full;
type Built = Ai2['built'][number];
type MineArtifact = Ai2['mine']['artifacts'][number];
type PanelSpread = Ai2['mine']['spreads'][number];
type PanelWorld = Ai2['data']['worlds'][number];

const Quiet = ({ children, testId }: { children: ReactNode; testId?: string }) => (
  <p className="m-0 text-sm text-muted-foreground" data-testid={testId}>
    {children}
  </p>
);

/** צ'יפ מצב: טיוטה / פורסם / מוקפא. */
function StatusChip({ status }: { status: string }) {
  return (
    <StatusLabel tone={status === 'published' ? 'ok' : status === 'draft' ? 'info' : 'neutral'} className="text-xs" data-testid="status-chip" data-status={status}>
      {T.ai.ai2.status[status] ?? status}
    </StatusLabel>
  );
}

const typeHe = (type: string) => (type === 'spread' ? T.ai.ai2.spread : T.artifacts.kinds[type] ?? type);
const artifactHref = (slug: string) => `/artifacts/${slug}`;

/** ריצה אחרונה של יצירה: "הסתיים · 09:40" / "נכשל: <סיבה>" באדום / "עוד לא הורצה". */
function LastRun({ run }: { run: Ai2['mine']['artifacts'][number]['lastRun'] }) {
  if (!run)
    return (
      <span className="text-xs text-muted-foreground" data-testid="last-run" data-status="none">
        {T.ai.ai2.noRun}
      </span>
    );
  const bad = run.status === 'failed' || run.status === 'timeout';
  return (
    <span className={cn('text-xs', bad ? 'text-error' : 'text-muted-foreground')} data-testid="last-run" data-status={run.status}>
      {bad && run.errorHe ? T.ai.ai2.failed(run.errorHe) : T.ai.ai2.lastRun(T.artifact.status[run.status] ?? run.status, run.finishedAt ? fmtClock(run.finishedAt) : '')}
    </span>
  );
}

/** שורת יצירה/פריסה: שם, סוג, מצב, "פתח" (ליצירה), ריצה אחרונה (ליצירה). */
function ItemRow({ kind, slug, nameHe, type, status, lastRun, extra, testId }: { kind: 'artifact' | 'spread'; slug: string; nameHe: string; type: string; status: string; lastRun?: Ai2['mine']['artifacts'][number]['lastRun']; extra?: ReactNode; testId: string }) {
  return (
    <li className="flex flex-col gap-1 rounded-md border border-border bg-card px-2 py-1.5 text-sm" data-testid={testId} data-kind={kind} data-key={slug}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{nameHe}</span>
        <span className="text-xs text-muted-foreground">{typeHe(type)}</span>
        <StatusChip status={status} />
        {kind !== 'spread' && (
          <Link to={artifactHref(slug)} className="ms-auto min-h-8 rounded-sm px-2 py-1 text-xs font-medium text-primary hover:bg-primary-soft" data-testid="open-item">
            {T.ai.ai2.open}
          </Link>
        )}
      </div>
      {kind === 'artifact' && <LastRun run={lastRun ?? null} />}
      {extra}
    </li>
  );
}

function BuiltRow({ b }: { b: Built }) {
  return (
    <ItemRow
      kind={b.kind}
      slug={b.key}
      nameHe={b.nameHe}
      type={b.type}
      status={b.status}
      lastRun={b.lastRun ?? null}
      testId="built-item"
      extra={
        <span className="text-xs text-muted-foreground">
          {T.ai.ai2.action[b.lastAction] ?? b.lastAction} · {fmtClock(b.at)}
        </span>
      }
    />
  );
}

function MineRow({ a }: { a: MineArtifact }) {
  return (
    <ItemRow
      kind="artifact"
      slug={a.slug}
      nameHe={a.nameHe}
      type={a.type}
      status={a.status}
      lastRun={a.lastRun}
      testId="mine-item"
      extra={
        a.spreads.length > 0 ? (
          <p className="m-0 text-xs text-muted-foreground" data-testid="mine-spreads">
            {T.ai.ai2.usesSpreads}: {a.spreads.map((s) => s.nameHe).join(', ')}
          </p>
        ) : undefined
      }
    />
  );
}

/** data-spread-publish-explain: תג "איטית (N שניות)" לפריסה שהמדידה בפרסום סימנה warn; ה-tooltip — מה לתקן (האזהרה הראשונה). */
export function SpreadPerfBadge({ perf }: { perf: PanelSpread['perf'] }) {
  if (!perf || perf.level !== 'warn') return null;
  return (
    <StatusLabel tone="warn" className="text-xs" data-testid="spread-perf-warn" title={perf.warningHe ?? undefined}>
      {T.ai.ai2.slow(Math.round(perf.ms / 1000))}
    </StatusLabel>
  );
}

export function SpreadRow({ s }: { s: PanelSpread }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 text-sm" data-testid="spread-item" data-key={s.key}>
      <span>{s.nameHe}</span>
      {s.domainHe && <span className="text-xs text-muted-foreground">{s.domainHe}</span>}
      {s.private && (
        <StatusLabel className="text-xs" data-testid="spread-private" title={T.ai.ai2.privateTitle}>
          {T.ai.ai2.private}
        </StatusLabel>
      )}
      <SpreadPerfBadge perf={s.perf} />
      <span className="ms-auto text-xs text-muted-foreground">{s.asOf ? T.ai.ai2.fresh(fmtClock(s.asOf)) : ''}</span>
    </li>
  );
}

/** עולם: "נתונים עדכניים ל-09:40" / תקוע / נכשל — באזהרה; מי שלא מנהל לא יכול לתקן. */
function WorldRow({ w, isAdmin }: { w: PanelWorld; isAdmin: boolean }) {
  const at = w.asOf ?? w.lastOkAt;
  const bad = w.status === 'stale' || w.status === 'failed';
  const text =
    w.status === 'failed' ? T.ai.ai2.syncFailed(w.reasonHe ?? '') : w.status === 'stale' ? (at ? T.ai.ai2.stale(fmtClock(at)) : T.ai.ai2.staleUnknown) : w.status === 'never' ? T.ai.ai2.never : T.ai.ai2.fresh(at ? fmtClock(at) : '');
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 text-sm" data-testid="world-item" data-world={w.key} data-status={w.status}>
      <span>{w.nameHe}</span>
      <span className={cn('text-xs', bad ? 'text-degraded' : 'text-muted-foreground')}>{text}</span>
      {bad &&
        (isAdmin ? (
          <Link to="/ai1" className="ms-auto text-xs text-primary">
            {T.ai.ai2.toAdmin}
          </Link>
        ) : (
          <span className="ms-auto text-xs text-muted-foreground">{T.ai.ai2.askAdmin}</span>
        ))}
    </li>
  );
}

/**
 * פאנל בינה-2 (11 §1): לפי מה שהמשתמש בא בשבילו, תשומת לב קודם — ממתין לך (רק כשיש), מה בנינו בשיחה הזו, היצירות שלי
 * (מכווץ כשיש הרבה), הקשר נתונים (מכווץ). בלי מונים שאינם ניתנים לפעולה. התצוגה המקדימה לא מצוירת כאן (web-ai-stage-layout):
 * היא בבמה של השיחה, ו-lastPreview.ref משמש את הבמה ל"פתח".
 */
export function Ai2Panel({ data }: { data: PanelAi2Full }) {
  const loc = useLocation();
  const s = useSession();
  const isAdmin = s?.user.role === 'admin';
  const w = data.waiting;
  const waitingN = w.consents.length + w.drafts.length + w.failedRuns.length;
  return (
    <div className="flex flex-col gap-5" data-testid="ai2-panel">
      {data.alerts.length > 0 && (
        // 19 §1.6: תזמון שנכשל/הושהה — קישור ללשונית "תזמונים" ביצירה
        <Section title={T.triggers.alertsTitle} testId="panel-trigger-alerts" count={data.alerts.length}>
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
            {data.alerts.map((al) => (
              <li key={al.id} data-testid="trigger-alert" data-kind={al.kind}>
                <Link to={`/artifacts/${al.trigger.slug}?tab=triggers`} className="flex min-h-11 items-center rounded-sm border border-error/40 bg-card px-3 py-2 text-error hover:border-error">
                  {al.messageHe}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {waitingN > 0 && (
        <Section title={T.ai.ai2.waiting} testId="panel-waiting">
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
            {w.consents.map((k) => (
              <li key={k.consentId} data-testid="waiting-consent">
                <Link to={consentHref(loc.search, k.consentId)} className="flex min-h-11 items-center rounded-sm bg-primary-soft px-3 font-medium text-primary">
                  {T.ai.ai2.consent(k.titleHe, k.itemsTotal)}
                </Link>
              </li>
            ))}
            {w.drafts.map((d) => (
              <li key={d.slug} data-testid="waiting-draft" data-key={d.slug}>
                <Link to={artifactHref(d.slug)} className="flex min-h-11 items-center gap-2 rounded-sm border border-border bg-card px-3 hover:border-primary/50">
                  <span className="me-auto">{T.ai.ai2.draft(d.nameHe)}</span>
                  <span className="text-xs text-primary">{T.ai.ai2.open}</span>
                </Link>
              </li>
            ))}
            {w.failedRuns.map((r) => (
              <li key={r.runId} data-testid="waiting-failed" data-key={r.slug}>
                <Link to={`/artifacts/${r.slug}/runs/${r.runId}`} className="flex min-h-11 flex-wrap items-center gap-x-2 rounded-sm border border-error/40 bg-card px-3 hover:border-error">
                  <span className="font-medium">{r.nameHe}</span>
                  <span className="text-error">{T.ai.ai2.failed(r.errorHe)}</span>
                  <span className="ms-auto text-xs text-muted-foreground">{r.finishedAt ? fmtClock(r.finishedAt) : ''}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {data.requests.length > 0 && (
        <Section title={T.ai.ai2.requests} testId="panel-my-requests">
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
            {data.requests.map((r) => (
              <MyRequestRow key={r.id} r={r} />
            ))}
          </ul>
        </Section>
      )}
      <Section title={T.ai.ai2.built} testId="panel-built">
        {data.built.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {data.built.map((b) => (
              <BuiltRow key={`${b.kind}:${b.key}`} b={b} />
            ))}
          </ul>
        ) : (
          <Quiet testId="built-empty">{data.conversationId ? T.ai.ai2.builtEmpty : T.ai.ai2.builtNoConversation}</Quiet>
        )}
      </Section>
      <details className="flex flex-col gap-2" open={data.mine.artifacts.length <= 5} data-testid="panel-mine">
        <summary className="cursor-pointer text-base font-semibold">{T.ai.ai2.mine}</summary>
        <div className="mt-2 flex flex-col gap-2">
          {data.mine.artifacts.length ? (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {data.mine.artifacts.map((a) => (
                <MineRow key={a.slug} a={a} />
              ))}
            </ul>
          ) : (
            <Quiet testId="mine-empty">{T.ai.ai2.mineEmpty}</Quiet>
          )}
          {data.mine.spreads.length > 0 && (
            <div className="flex flex-col gap-1" data-testid="panel-spreads">
              <h3 className="m-0 text-xs font-semibold text-muted-foreground">{T.ai.ai2.spreads}</h3>
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                {data.mine.spreads.map((sp) => (
                  <SpreadRow key={sp.key} s={sp} />
                ))}
              </ul>
            </div>
          )}
        </div>
      </details>
      <details className="flex flex-col gap-2" data-testid="panel-data">
        <summary className="cursor-pointer text-sm font-semibold text-muted-foreground">{T.ai.ai2.data}</summary>
        {data.data.worlds.length ? (
          <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
            {data.data.worlds.map((wd) => (
              <WorldRow key={wd.key} w={wd} isAdmin={isAdmin} />
            ))}
          </ul>
        ) : (
          <Quiet>{T.ai.ai2.noWorlds}</Quiet>
        )}
      </details>
    </div>
  );
}

/** בקשה שאייבי העבירה בשם המשתמש: מצב, ואחרי סגירה התוצאה (או סיבת הדחייה). */
function MyRequestRow({ r }: { r: MyAdminRequest }) {
  const tone = r.status === 'done' ? 'ok' : r.status === 'declined' ? 'error' : 'neutral';
  const note = r.status === 'done' ? r.resultHe : r.status === 'declined' ? r.decisionNoteHe : null;
  return (
    <li className="flex flex-col gap-1 rounded-sm border border-border bg-card px-3 py-2" data-testid="my-request" data-request-id={r.id} data-status={r.status}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="me-auto font-medium">{r.titleHe}</span>
        <StatusLabel tone={tone} className="text-xs">
          {T.ai.ai2.requestStatus[r.status] ?? r.status}
        </StatusLabel>
      </div>
      {note && (
        <p className="m-0 text-xs text-muted-foreground" data-testid="my-request-note">
          {note}
        </p>
      )}
    </li>
  );
}

/** בקשת משתמש אצל מנהל הבינה (ai1): מי ביקש, מה צריך, מה אייבי מצאה; טפל בזה (פותח שיחה ועובר אליה) או דחה עם סיבה. */
function RequestCard({ r }: { r: AdminRequestCard }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const R = T.ai.requests;
  const decide = async (d: 'approve' | 'decline') => {
    if (d === 'decline' && !note.trim()) return setErr(R.reasonRequired);
    setBusy(true);
    setErr(null);
    try {
      const out = await aiApi.decideRequest(r.id, d, d === 'decline' ? note.trim() : '');
      await qc.invalidateQueries({ queryKey: ['panel', 1] });
      if (out.conversationId) nav(`/ai1/${out.conversationId}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.textHe : T.network);
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex flex-col gap-2 rounded-md border border-border bg-card p-3" data-testid="admin-request" data-request-id={r.id} data-status={r.status}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="rounded-sm bg-primary-soft px-2 font-medium">{R.kind[r.kind] ?? r.kind}</span>
        {r.status === 'in_progress' &&
          (r.readyAt ? (
            <StatusLabel tone="info" className="text-xs" data-testid="request-ready">
              {R.ready}
            </StatusLabel>
          ) : (
            <StatusLabel className="text-xs">{R.inProgress}</StatusLabel>
          ))}
        <span className="ms-auto text-xs text-muted-foreground">{when(r.createdAt)}</span>
      </div>
      <h3 className="m-0 text-base font-semibold" data-testid="request-title">
        {r.titleHe}
      </h3>
      {r.status === 'in_progress' && r.readyAt && <p className="m-0 text-xs text-muted-foreground">{R.readyHint}</p>}
      <p className="m-0 text-xs text-muted-foreground" data-testid="request-by">
        {R.by(r.requestedBy.map((u) => u.nameHe || '?').join(', '))}
      </p>
      <div className="text-sm" data-testid="request-need">
        <span className="font-medium">{R.need}: </span>
        {r.needHe}
      </div>
      {r.findingsHe && (
        <div className="whitespace-pre-line text-sm text-muted-foreground" data-testid="request-findings">
          <span className="font-medium text-foreground">{R.findings}: </span>
          {r.findingsHe}
        </div>
      )}
      {err && (
        <p role="alert" className="m-0 text-sm text-error" data-testid="request-error">
          {err}
        </p>
      )}
      {r.status === 'open' && declining && (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`req-note-${r.id}`}>{R.reason}</Label>
          <Input id={`req-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} data-testid="request-reason" />
        </div>
      )}
      {r.status === 'open' ? (
        <div className="flex gap-2">
          {declining ? (
            <>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => void decide('decline')} data-testid="request-decline-confirm">
                {R.declineConfirm}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => (setDeclining(false), setErr(null))}>
                {R.cancel}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" disabled={busy} onClick={() => void decide('approve')} data-testid="request-handle">
                {R.handle}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setDeclining(true)} data-testid="request-decline">
                {R.decline}
              </Button>
            </>
          )}
        </div>
      ) : (
        r.adminConversationId && (
          <Link to={`/ai1/${r.adminConversationId}`} className="text-sm text-primary" data-testid="request-conversation">
            {R.openConversation}
          </Link>
        )
      )}
    </li>
  );
}

/* ─── ai1 ─── */

/**
 * whatChanges (05 gate.whatChanges): { summaryHe, counts, items[{op, path, nameHe, detail}] } → משפט במילים ("מוסיף הגדרה אחת, משנה 2 שדות")
 * + summaryHe; הצמתים (נתיבים) ו-counts — רק ב"פרטים". צורת ה-mock הישנה (items[{labelHe, count, names}]) מוצגת כרשימה כמו קודם.
 * לעולם לא JSON גולמי על הכרטיס (F20).
 */
export function WhatChanges({ value }: { value: unknown }) {
  if (!value || typeof value !== 'object') return <p className="m-0 text-sm">{String(value ?? '')}</p>;
  const v = value as { summaryHe?: unknown; items?: unknown };
  const api = hasApiItems(v);
  const sentence = api ? changesSentenceHe(v) : '';
  const items = !api && Array.isArray(v.items) ? (v.items as Array<{ labelHe?: string; count?: number; names?: string[] }>) : null;
  const rest = api ? [] : Object.entries(value as Record<string, unknown>).filter(([k]) => k !== 'summaryHe' && k !== 'items' && k !== 'counts');
  return (
    <div className="flex flex-col gap-1 text-sm" data-testid="what-changes">
      {sentence && (
        <p className="m-0 font-medium" data-testid="changes-sentence">
          {sentence}
        </p>
      )}
      {typeof v.summaryHe === 'string' && <p className={cn('m-0', sentence ? 'text-muted-foreground' : 'font-medium')}>{v.summaryHe}</p>}
      {items && (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {items.map((it, i) => (
            <li key={i}>
              {it.labelHe}
              {typeof it.count === 'number' && (
                <>
                  : <bdi className="font-semibold tabular-nums">{it.count.toLocaleString('he-IL')}</bdi>
                </>
              )}
              {it.names?.length ? <span className="text-muted-foreground"> ({it.names.join(', ')})</span> : null}
            </li>
          ))}
        </ul>
      )}
      {rest.length > 0 && (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-2">
          {rest.map(([k, x]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">
                <bdi dir="ltr">{k}</bdi>
              </dt>
              <dd className="m-0">
                <bdi dir="auto">{typeof x === 'object' ? JSON.stringify(x) : String(x)}</bdi>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/** "פרטים" של הצעה: הצמתים (נתיב + שינוי), הראיות המלאות (מתורגמות), ומזהים פנימיים — מכווץ, לא על הכרטיס. */
function ProposalDetails({ p }: { p: PendingProposal }) {
  const w = p.whatChanges as { items?: unknown; counts?: unknown } | null;
  const nodes = w && Array.isArray(w.items) ? (w.items as ChangeItem[]).filter((i) => typeof i.path === 'string') : [];
  const actor = typeof p.proposedBy === 'string' ? p.proposedBy : p.proposedBy.actor;
  return (
    <details className="text-xs text-muted-foreground" data-testid="proposal-details">
      <summary className="cursor-pointer">{T.ai.proposalDetails}</summary>
      <div className="mt-1 flex flex-col gap-1">
        {p.rationaleHe && (
          <p className="m-0">
            <span className="font-medium">{T.ai.proposalEvidence}: </span>
            {humanizeEvidenceHe(p.rationaleHe)}
          </p>
        )}
        {nodes.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {nodes.map((n, i) => (
              <li key={i}>
                <bdi dir="ltr">{n.path}</bdi>
                {n.nameHe ? ': ' + n.nameHe : ''}
                {n.detail ? ' (' + n.detail + ')' : ''}
              </li>
            ))}
          </ul>
        )}
        <p className="m-0">
          <bdi dir="ltr">{p.proposalId}</bdi>
          {actor ? <> · <bdi dir="ltr">{actor}</bdi></> : null}
        </p>
      </div>
    </details>
  );
}

function ProposalCard({ p }: { p: PendingProposal }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const by = proposerHe(p.proposedBy);
  const title = proposalTitleHe(p.whatChanges as never, T.ai.proposalKind[p.kind] ?? p.kind);
  const evidence = evidenceSummaryHe(p.rationaleHe);
  const decide = async (d: 'approve' | 'reject') => {
    setBusy(d);
    setErr(null);
    try {
      await aiApi.decideProposal(p.proposalId, d);
      await qc.invalidateQueries({ queryKey: ['panel', 1] });
    } catch (e) {
      setErr(e instanceof ApiError ? e.textHe : T.network);
    } finally {
      setBusy(null);
    }
  };
  return (
    <li className="flex flex-col gap-2 rounded-md border border-border bg-card p-3" data-testid="proposal" data-proposal-id={p.proposalId} data-kind={p.kind}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="rounded-sm bg-primary-soft px-2 font-medium">{T.ai.proposalKind[p.kind] ?? p.kind}</span>
        {p.worldNameHe && <span className="text-muted-foreground">{p.worldNameHe}</span>}
        <span className="ms-auto text-xs text-muted-foreground">{when(p.createdAt)}</span>
      </div>
      <h3 className="m-0 text-base font-semibold" data-testid="proposal-title">
        {title}
      </h3>
      <WhatChanges value={p.whatChanges} />
      {evidence && (
        <p className="m-0 text-xs text-muted-foreground" data-testid="proposal-evidence">
          {evidence}
        </p>
      )}
      {by && (
        <p className="m-0 text-xs text-muted-foreground" data-testid="proposal-by">
          {T.ai.proposedBy(by)}
        </p>
      )}
      <ProposalDetails p={p} />
      {err && (
        <p role="alert" className="m-0 text-sm text-error" data-testid="proposal-error">
          {err}
        </p>
      )}
      <div className="flex gap-2">
        <Button size="sm" disabled={!!busy} onClick={() => void decide('approve')} data-testid="proposal-approve">
          {T.ai.approve}
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => void decide('reject')} data-testid="proposal-reject">
          {T.ai.reject}
        </Button>
      </div>
    </li>
  );
}

/** שורת היסטוריה: הצעה שאושרה/נדחתה/בוטלה (בינה-1)/הוחלפה — לא ממתינה, בלי כפתורים. */
function RecentProposalRow({ p }: { p: RecentProposal }) {
  const by = proposerHe(p.proposedBy);
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm" data-testid="recent-proposal" data-proposal-id={p.proposalId} data-state={p.state}>
      <StatusLabel tone={p.state === 'approved' ? 'ok' : p.state === 'rejected' ? 'error' : 'neutral'} className="text-xs">
        {T.ai.proposalState[p.state] ?? p.state}
      </StatusLabel>
      <span className="text-xs text-muted-foreground">{T.ai.proposalKind[p.kind] ?? p.kind}</span>
      {p.summaryHe && <span>{p.summaryHe}</span>}
      {p.noteHe && <span className="text-xs text-muted-foreground">{p.noteHe}</span>}
      <span className="ms-auto text-xs text-muted-foreground" title={by ? T.ai.proposedBy(by) : undefined}>
        {by && <span data-testid="recent-by">{by} · </span>}
        {when(p.decidedAt ?? p.createdAt)}
      </span>
    </li>
  );
}

type World = PanelAi1Full['worlds'][number];
type SyncRun = PanelAi1Full['syncRuns'][number];

const uniq = <T,>(xs: T[]) => [...new Set(xs)];

/** כותרת הבקשה: שמות העולמות; החברות — בשורת הפרטים. */
const pairingWorldsHe = (p: PendingPairing) => uniq(p.worlds.map((w) => w.nameHe || w.key)).join(', ');
const pairingCompaniesHe = (p: PendingPairing) => {
  const cos = uniq(p.worlds.flatMap((w) => (w.company ? [w.company] : [])));
  return !cos.length ? null : cos.length === 1 ? `חברה ${cos[0]}` : `חברות ${cos.join(', ')}`;
};

/** connRef → חברה (ל-db של MSSQL: DB לכל חברה, ברירת מחדל = קוד החברה). */
const companyOf = (p: PendingPairing, ref: string) => p.worlds.find((w) => w.company && ref === `${w.key}.${w.company}`)?.company ?? '';

function PairingRequest({ p, onDone }: { p: PendingPairing; onDone: (text: string) => void }) {
  // הסיסמה: שדה לא-מבוקר (ref) — לא ב-state של React, לא ב-storage; מתרוקן אחרי שליחה.
  const qc = useQueryClient();
  const pw = useRef<HTMLInputElement>(null);
  const [driver, setDriver] = useState<'mssql' | 'oracle'>(p.worlds[0]?.driver === 'oracle' ? 'oracle' : 'mssql');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refs = p.connRefs;
  const id = (n: string) => `pair-${p.pairingId}-${n}`;
  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const get = (k: string) => String(f.get(k) ?? '').trim();
    const password = pw.current?.value ?? '';
    const port = Number(get('port'));
    const shared = { host: get('host'), ...(port ? { port } : {}), user: get('user'), password };
    // MSSQL: DB לכל connRef (חברה); Oracle: service אחד לכולם (החברות מובחנות בקידומת הטבלאות).
    // Oracle Thick (change connector-oracle-thick-mode): תיקיית ה-Oracle Client על המארח → options.clientLibDir (לא סוד, ריק = Thin)
    const clientLibDir = get('clientLibDir');
    const oracle = { ...shared, service: get('service'), ...(clientLibDir ? { options: { clientLibDir } } : {}) };
    const bundle = Object.fromEntries(refs.map((r, i) => [r, driver === 'mssql' ? { ...shared, db: get(`db-${i}`) } : oracle]));
    setBusy(true);
    setErr(null);
    try {
      const r = await aiApi.attachPairingSecrets(p.pairingId, bundle);
      onDone(T.ai.pairing.ok(r.connRefs.join(', ')));
      await qc.invalidateQueries({ queryKey: ['panel', 1] });
    } catch (x) {
      setErr(x instanceof ApiError ? x.textHe : T.network);
    } finally {
      if (pw.current) pw.current.value = '';
      setBusy(false);
    }
  };
  const field = (name: string, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id(name)}>{label}</Label>
      <Input id={id(name)} name={name} dir="ltr" autoComplete="off" spellCheck={false} {...props} />
    </div>
  );
  return (
    <section
      className="flex flex-col gap-2 rounded-md border border-action/40 bg-card p-3 shadow-sm"
      data-testid="panel-pairing"
      data-pairing-id={p.pairingId}
      aria-labelledby={id('title')}
    >
      <h2 id={id('title')} className="m-0 text-base font-semibold">
        {T.ai.pairing.title(pairingWorldsHe(p))}
      </h2>
      <p className="m-0 text-xs text-muted-foreground" data-testid="pairing-why">
        {T.ai.pairing.why}
      </p>
      <p className="m-0 text-xs text-muted-foreground" data-testid="pairing-meta">
        {pairingCompaniesHe(p) && <>{pairingCompaniesHe(p)} · </>}
        {T.ai.pairing.expires(fmtClock(p.expiresAt))}
      </p>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-2" data-testid="pairing-form" autoComplete="off">
        <div className="flex flex-col gap-1">
          <Label htmlFor={id('driver')}>{T.ai.pairing.driver}</Label>
          <Select id={id('driver')} value={driver} onChange={(e) => setDriver(e.target.value as 'mssql' | 'oracle')}>
            <option value="mssql">{T.ai.pairing.mssql}</option>
            <option value="oracle">{T.ai.pairing.oracle}</option>
          </Select>
        </div>
        <div className="grid grid-cols-[1fr_6rem] gap-2">
          {field('host', T.ai.pairing.host, { required: true })}
          {field('port', T.ai.pairing.port, { inputMode: 'numeric', pattern: '[0-9]*' })}
        </div>
        {driver === 'mssql'
          ? refs.map((r, i) => (
              <div key={r}>{field(`db-${i}`, refs.length > 1 ? T.ai.pairing.dbFor(r) : T.ai.pairing.db, { required: true, defaultValue: companyOf(p, r) })}</div>
            ))
          : field('service', T.ai.pairing.service, { required: true })}
        {driver === 'oracle' && (
          <div className="flex flex-col gap-1">
            <Label htmlFor={id('clientLibDir')}>{T.ai.pairing.clientLibDir}</Label>
            <Input id={id('clientLibDir')} name="clientLibDir" dir="ltr" autoComplete="off" spellCheck={false} data-testid="pairing-client-lib-dir" />
            <p className="m-0 text-xs text-muted-foreground">{T.ai.pairing.clientLibDirHint}</p>
          </div>
        )}
        {field('user', T.ai.pairing.user, { required: true })}
        <div className="flex flex-col gap-1">
          <Label htmlFor={id('password')}>{T.ai.pairing.password}</Label>
          {/* בלי name: לא נכנס ל-FormData, לא מוצע לשמירה על ידי הדפדפן */}
          <Input id={id('password')} ref={pw} type="password" dir="ltr" autoComplete="new-password" required data-testid="pairing-password" />
        </div>
        {err && (
          <p role="alert" className="m-0 text-sm text-error" data-testid="pairing-msg" data-ok="false">
            {err}
          </p>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? T.ai.pairing.busy : T.ai.pairing.submit}
        </Button>
      </form>
    </section>
  );
}

const runOk = (s: string) => s === 'ok' || s === 'done';
const runBad = (s: string) => s === 'failed' || s === 'error';

/** שורת הסנכרון לעולם. בלי `sync` מה-api (mock ישן) — נגזרת מ-lastSync בלבד. */
function SyncLine({ w }: { w: World }) {
  const s = w.sync ?? (w.lastSync ? { status: 'ok' as const, lastOkAt: w.lastSync } : { status: 'never' as const });
  const tone = s.status === 'ok' ? 'text-ok' : s.status === 'stale' ? 'text-degraded' : s.status === 'failed' ? 'text-error' : 'text-muted-foreground';
  const text =
    s.status === 'ok'
      ? T.ai.sync.ok(fmtAgo(s.lastOkAt))
      : s.status === 'stale'
        ? s.since
          ? T.ai.sync.stale(fmtClock(s.since))
          : T.ai.sync.staleUnknown
        : s.status === 'failed'
          ? T.ai.sync.failed(s.reasonHe)
          : s.status === 'running'
            ? T.ai.sync.running
            : T.ai.sync.never;
  return (
    <span className={cn('flex min-w-0 items-center gap-1.5 text-sm', tone)} data-testid="sync-line" data-status={s.status}>
      <span className={cn('size-2 shrink-0 rounded-circle', s.status === 'ok' ? 'bg-ok' : s.status === 'stale' ? 'bg-degraded' : s.status === 'failed' ? 'bg-error' : 'bg-muted-foreground')} aria-hidden="true" />
      <span className="min-w-0 truncate" title={text}>
        <bdi>{text}</bdi>
      </span>
    </span>
  );
}

const syncAlert = (w: World) => w.alerts.some((a) => a.kind === 'sync.failed' || a.kind === 'sync.stale');
/** דורש תשומת לב: התראה פתוחה, סנכרון לא תקין או קונקטור מנותק. */
const needsAttention = (w: World) => w.alerts.length > 0 || w.connector === 'disconnected' || w.sync?.status === 'failed' || w.sync?.status === 'stale';

function WorldCard({ w, runs }: { w: World; runs: SyncRun[] }) {
  // נפתח לבד כשיש התראת סנכרון פתוחה; אחרת — לחיצה
  const [open, setOpen] = useState(syncAlert(w));
  const other = w.alerts.filter((a) => a.kind !== 'sync.failed' && a.kind !== 'sync.stale');
  const syncAlerts = w.alerts.filter((a) => a.kind === 'sync.failed' || a.kind === 'sync.stale');
  const runsId = `runs-${w.key}`;
  return (
    <li
      className={cn('flex flex-col gap-1 rounded-md border bg-card px-2 py-1.5', needsAttention(w) ? 'border-degraded/50' : 'border-border')}
      data-testid="world"
      data-world={w.key}
      data-connector={w.connector}
    >
      <div className="flex items-center gap-2">
        <span className="font-medium">{w.nameHe}</span>
        <bdi dir="ltr" className="text-xs text-muted-foreground">
          {w.key}
        </bdi>
        <StatusLabel
          tone={w.connector === 'online' ? 'ok' : w.connector === 'disconnected' ? 'error' : 'neutral'}
          className="ms-auto text-xs"
          title={w.lastSeen ? `${T.ai.lastSeen}: ${fmtWhen(w.lastSeen)}` : undefined}
        >
          {T.ai.connector[w.connector]}
        </StatusLabel>
      </div>
      {other.map((a) => (
        <span key={a.id} className="text-xs text-degraded" data-testid="world-alert" data-kind={a.kind}>
          {a.messageHe}
        </span>
      ))}
      <button
        type="button"
        className="-mx-1 flex min-h-9 items-center gap-1 rounded px-1 text-start hover:bg-muted"
        aria-expanded={open}
        aria-controls={runsId}
        onClick={() => setOpen((o) => !o)}
        data-testid="sync-toggle"
      >
        <SyncLine w={w} />
        <span className={cn('ms-auto shrink-0 text-base leading-none text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div id={runsId} className="flex flex-col gap-1 border-t border-border pt-1" data-testid="sync-runs">
          {syncAlerts.map((a) => (
            <p key={a.id} className={cn('m-0 text-xs', a.kind === 'sync.failed' ? 'text-error' : 'text-degraded')} data-testid="world-alert" data-kind={a.kind}>
              {a.messageHe}
            </p>
          ))}
          {w.lastSync && (
            <p className="m-0 text-xs text-muted-foreground">
              {T.ai.sync.lastOk}: {when(w.lastSync)}
            </p>
          )}
          {!!w.tables?.length && (
            <>
              <h3 className="m-0 text-xs font-semibold text-muted-foreground">{T.ai.sync.tables}</h3>
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-xs" data-testid="sync-tables">
                {w.tables.map((t) => (
                  <li key={`${t.entity}@${t.company}`} className="flex flex-wrap items-center gap-x-2" data-testid="sync-table" data-signal={t.signal ?? ''} data-fallback={t.fallback ? '1' : '0'}>
                    <span className={cn('size-1.5 rounded-circle', t.fallback ? 'bg-degraded' : t.loading ? 'bg-primary' : 'bg-ok')} aria-hidden="true" />
                    <span className="font-medium">{t.entity}</span>
                    <span className="text-muted-foreground">{t.company}</span>
                    <span title={t.fallback?.reasonHe ?? ''}>
                      {t.loading ? T.ai.sync.loading(t.loading.done, t.loading.total) : T.ai.sync.signal[t.signal ?? 'none'] ?? t.signal}
                      {t.fallback && <span className="text-degraded"> · {T.ai.sync.fallback(T.ai.sync.signal[t.fallback.to] ?? t.fallback.to)}</span>}
                    </span>
                    <span className="ms-auto text-muted-foreground">
                      {t.asOf && `${T.ai.sync.asOf} ${when(t.asOf)}`}
                      {' · '}
                      {t.verifiedAt ? `${T.ai.sync.verifiedAt} ${when(t.verifiedAt)}` : T.ai.sync.notVerified}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <h3 className="m-0 text-xs font-semibold text-muted-foreground">{T.ai.sync.runs}</h3>
          {runs.length ? (
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-xs">
              {runs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-2" data-testid="sync-run" data-status={r.status}>
                  <span className={cn('size-1.5 rounded-circle', runOk(r.status) ? 'bg-ok' : runBad(r.status) ? 'bg-error' : r.status === 'partial' ? 'bg-degraded' : 'bg-primary')} aria-hidden="true" />
                  <span className={cn(runBad(r.status) && 'text-error')}>{T.ai.sync.run[r.status] ?? r.status}</span>
                  {r.summaryHe && <span className="min-w-0 text-muted-foreground">{r.summaryHe}</span>}
                  <span className="ms-auto text-muted-foreground">{when(r.finishedAt ?? r.startedAt)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-xs text-muted-foreground">{T.ai.sync.noRuns}</p>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * מה שמחכה להחלטת האדם בפאנל מנהל הבינה (באג זבידה 26.10: "1 ממתינים לך" בלי לדעת מה) — קונסנטים, בקשות פרטי חיבור,
 * בקשות משתמשים פתוחות (טרם הוחלט) והצעות ממתינות. מקור אחד למונה (Ai.tsx) ולכותרת "ממתין לך" — מה שנספר הוא מה שמוצג.
 */
export function ai1Waiting(data: Partial<Pick<PanelAi1Full, 'consentsPending' | 'pendingPairings' | 'pendingProposals' | 'requests'>> | null | undefined) {
  const openRequests = (data?.requests ?? []).filter((r) => r.status === 'open');
  // conversation-request-close-on-publish: העבודה פורסמה והבקשה לא נסגרה — גלוי לאדם, לא מוסתר בין "בטיפול"
  const readyRequests = (data?.requests ?? []).filter((r) => r.status === 'in_progress' && !!r.readyAt);
  const consents = data?.consentsPending ?? 0;
  const pairings = data?.pendingPairings ?? [];
  const proposals = data?.pendingProposals ?? [];
  return { consents, pairings, openRequests, readyRequests, proposals, count: consents + pairings.length + openRequests.length + readyRequests.length + proposals.length };
}

export function Ai1Panel({ data }: { data: PanelAi1Full }) {
  const loc = useLocation();
  const [pairedMsg, setPairedMsg] = useState<string | null>(null);
  // מה שמחכה לאדם — קודם, תחת "ממתין לך": קונסנטים, בקשות פרטי חיבור, בקשות פתוחות, הצעות ממתינות. אחר כך מה שבטיפול והעולמות
  const waiting = ai1Waiting(data);
  const inProgress = data.requests.filter((r) => r.status !== 'open' && !(r.status === 'in_progress' && r.readyAt));
  const worlds = [...data.worlds].sort((a, b) => Number(needsAttention(b)) - Number(needsAttention(a)));
  const runsOf = (k: string) => data.syncRuns.filter((r) => r.worldKey === k);
  return (
    <div className="flex flex-col gap-5" data-testid="ai1-panel">
      {pairedMsg && (
        <p role="status" className="m-0 rounded-md bg-ok/10 px-3 py-2 text-sm text-ok" data-testid="pairing-msg" data-ok="true">
          {pairedMsg}
        </p>
      )}
      {waiting.count > 0 && (
        <Section title={T.ai.ai2.waiting} testId="panel-waiting" count={waiting.count}>
          {waiting.consents > 0 && (
            <Link to={consentHref(loc.search, 'all')} className="rounded-md bg-primary-soft px-3 py-2 text-sm font-medium text-primary" data-testid="panel-consents">
              {T.ai.consentsPending(waiting.consents)}
            </Link>
          )}
          {waiting.pairings.map((p) => (
            <PairingRequest key={p.pairingId} p={p} onDone={setPairedMsg} />
          ))}
          {waiting.openRequests.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="panel-requests">
              {waiting.openRequests.map((r) => (
                <RequestCard key={r.id} r={r} />
              ))}
            </ul>
          )}
          {waiting.readyRequests.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="panel-requests-ready">
              {waiting.readyRequests.map((r) => (
                <RequestCard key={r.id} r={r} />
              ))}
            </ul>
          )}
          {waiting.proposals.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="panel-proposals">
              {waiting.proposals.map((p) => (
                <ProposalCard key={p.proposalId} p={p} />
              ))}
            </ul>
          )}
        </Section>
      )}
      {data.alerts.length > 0 && (
        <Section title={T.ai.systemAlerts} testId="panel-system-alerts" count={data.alerts.length}>
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
            {data.alerts.map((a) => (
              <li key={a.id} className="rounded-md border border-degraded/50 bg-card px-2 py-1.5 text-xs text-error" data-testid="system-alert" data-kind={a.kind}>
                {a.messageHe}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {inProgress.length > 0 && (
        <Section title={T.ai.requests.inProgressTitle} testId="panel-requests-in-progress" count={inProgress.length}>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {inProgress.map((r) => (
              <RequestCard key={r.id} r={r} />
            ))}
          </ul>
        </Section>
      )}
      <Section title={T.ai.worlds} testId="panel-worlds" count={data.worlds.length}>
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-sm">
          {worlds.map((w) => (
            <WorldCard key={w.key} w={w} runs={runsOf(w.key)} />
          ))}
        </ul>
      </Section>
      {/* הצעות ממתינות מוצגות למעלה ב"ממתין לך"; כאן — "אין הצעות" כשאין, וההיסטוריה */}
      {(waiting.proposals.length === 0 || data.recentProposals.length > 0) && (
        <Section title={T.ai.proposals} testId="panel-proposals-history">
          {waiting.proposals.length === 0 && <p className="m-0 text-sm text-muted-foreground">{T.ai.noProposals}</p>}
          {data.recentProposals.length > 0 && (
            <details className="text-sm" data-testid="recent-proposals">
              <summary className="cursor-pointer text-muted-foreground">
                {T.ai.recentProposals} ({data.recentProposals.length})
              </summary>
              <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
                {data.recentProposals.map((p) => (
                  <RecentProposalRow key={p.proposalId} p={p} />
                ))}
              </ul>
            </details>
          )}
        </Section>
      )}
      <Section title={T.ai.users} testId="panel-users" count={data.users.length}>
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
          {data.users.map((u) => (
            <UserRow key={u.id} u={u} />
          ))}
        </ul>
      </Section>
    </div>
  );
}

/** שורת משתמש בפאנל מנהל בינה + 26: "קישור כניסה" (admin; השרת אוכף) — הקישור מוצג פעם אחת, עם העתקה. */
function UserRow({ u }: { u: PanelAi1Full['users'][number] }) {
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const create = async () => {
    setBusy(true);
    setErr('');
    setCopied(false);
    try {
      setLink(await aiApi.loginLink(u.id));
    } catch (e) {
      setErr(e instanceof ApiError ? e.textHe : T.ai.loginLink.failed);
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex flex-col gap-1" data-testid="panel-user" data-user={u.id}>
      <div className="flex items-center gap-2">
        <span className={cn(!u.active && 'text-muted-foreground line-through')}>{u.nameHe}</span>
        <span className="ms-auto text-xs text-muted-foreground">
          {T.roles[u.role]}
          {!u.active && ` · ${T.ai.inactive}`}
        </span>
        {u.active && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busy} onClick={() => void create()} data-testid="login-link-btn">
            {T.ai.loginLink.button}
          </Button>
        )}
      </div>
      {err && (
        <p role="alert" className="m-0 text-xs text-error">
          {err}
        </p>
      )}
      {link && (
        <div className="flex flex-col gap-1.5 rounded-md border border-border bg-card p-2 text-xs" data-testid="login-link-box">
          <p className="m-0 font-semibold">{T.ai.loginLink.title(u.nameHe)}</p>
          <input readOnly dir="ltr" value={link.url} className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-[11px]" onFocus={(e) => e.currentTarget.select()} data-testid="login-link-url" aria-label={T.ai.loginLink.title(u.nameHe)} />
          <p className="m-0 text-muted-foreground">{T.ai.loginLink.until(fmtClock(link.expiresAt))}</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => {
                void navigator.clipboard?.writeText(link.url).then(() => setCopied(true), () => undefined);
              }}
              data-testid="login-link-copy"
            >
              {copied ? T.ai.loginLink.copied : T.ai.loginLink.copy}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setLink(null)}>
              {T.ai.loginLink.close}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
